import type {
  DocKind,
  DocStatus,
  DocumentDetail,
  DocumentSummary,
  HoldingRow,
  InventoryDetail,
  InventoryStatus,
  InventorySummary,
  Item,
  MovementRow,
  Person,
  QcStatus,
  Reservation,
  StockRow,
  ToolEvent,
  ToolState,
  ToolUnit,
  Warehouse,
} from '@epc/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { Tone } from '../../ui/feedback';

export const warehousesQuery = queryOptions({
  queryKey: ['wh', 'warehouses'],
  queryFn: () => api<Warehouse[]>('/warehouse/warehouses'),
  staleTime: 30_000,
});

export const itemsQuery = (q?: { q?: string; kind?: 'material' | 'tool'; all?: boolean }) =>
  queryOptions({
    queryKey: ['wh', 'items', q ?? {}],
    queryFn: () =>
      api<Item[]>('/warehouse/items', {
        query: { q: q?.q, kind: q?.kind, all: q?.all ? 'true' : undefined },
      }),
  });

export const itemQuery = (id: string) =>
  queryOptions({ queryKey: ['wh', 'item', id], queryFn: () => api<Item>(`/warehouse/items/${id}`) });

export const stockQuery = (q: { warehouseId?: string; projectId?: string; itemId?: string; q?: string }) =>
  queryOptions({
    queryKey: ['wh', 'stock', q],
    queryFn: () => api<StockRow[]>('/warehouse/stock', { query: q }),
  });

export const holdingsQuery = (q: { userId?: string; itemId?: string } = {}) =>
  queryOptions({
    queryKey: ['wh', 'holdings', q],
    queryFn: () => api<HoldingRow[]>('/warehouse/holdings', { query: q }),
  });

export const movementsQuery = (q: {
  warehouseId?: string;
  itemId?: string;
  documentId?: string;
  limit?: number;
}) =>
  queryOptions({
    queryKey: ['wh', 'movements', q],
    queryFn: () => api<MovementRow[]>('/warehouse/movements', { query: q }),
  });

export const documentsQuery = (q: { kind?: DocKind; warehouseId?: string; status?: DocStatus; q?: string }) =>
  queryOptions({
    queryKey: ['wh', 'documents', q],
    queryFn: () => api<DocumentSummary[]>('/warehouse/documents', { query: q }),
  });

export const documentQuery = (id: string) =>
  queryOptions({
    queryKey: ['wh', 'document', id],
    queryFn: () => api<DocumentDetail>(`/warehouse/documents/${id}`),
  });

export const reservationsQuery = (q: { warehouseId?: string; itemId?: string; all?: boolean } = {}) =>
  queryOptions({
    queryKey: ['wh', 'reservations', q],
    queryFn: () =>
      api<Reservation[]>('/warehouse/reservations', { query: { ...q, all: q.all ? 'true' : undefined } }),
  });

export const peopleQuery = queryOptions({
  queryKey: ['people'],
  queryFn: () => api<Person[]>('/people'),
  staleTime: 60_000,
});

export const DOC_TONE: Record<DocStatus, Tone> = {
  draft: 'neutral',
  review: 'warning',
  posted: 'success',
  accepted: 'success',
  cancelled: 'neutral',
};

export const QC_TONE: Record<QcStatus, Tone> = {
  none: 'neutral',
  pending: 'warning',
  accepted: 'success',
  rejected: 'danger',
};

const qtyFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 3 });
export const qty = (n: number | null | undefined) => (n === null || n === undefined ? '—' : qtyFmt.format(n));

export function parseNum(v: string): number {
  return Number(v.replace(/\s/g, '').replace(',', '.'));
}

/** Ключ формы, чтобы повторная отправка не создала второй документ (СК-04) */
export function formKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/* ───────────── Инвентаризация и инструмент ───────────── */

export const inventoriesQuery = (q: { warehouseId?: string; status?: InventoryStatus } = {}) =>
  queryOptions({
    queryKey: ['wh', 'inventories', q],
    queryFn: () => api<InventorySummary[]>('/warehouse/inventories', { query: q }),
  });

export const inventoryQuery = (id: string) =>
  queryOptions({
    queryKey: ['wh', 'inventory', id],
    queryFn: () => api<InventoryDetail>(`/warehouse/inventories/${id}`),
  });

export const inventoryLastQuery = queryOptions({
  queryKey: ['wh', 'inventories', 'last'],
  queryFn: () =>
    api<Array<{ warehouseId: string; lastApprovedAt: string | null; overdue: boolean }>>(
      '/warehouse/inventories/last',
    ),
});

export const toolsQuery = (
  q: { warehouseId?: string; holderUserId?: string; q?: string; flag?: string } = {},
) =>
  queryOptions({
    queryKey: ['wh', 'tools', q],
    queryFn: () => api<ToolUnit[]>('/warehouse/tools', { query: q }),
  });

export const toolQuery = (id: string) =>
  queryOptions({
    queryKey: ['wh', 'tool', id],
    queryFn: () => api<ToolUnit & { events: ToolEvent[] }>(`/warehouse/tools/${id}`),
  });

export const INVENTORY_TONE: Record<InventoryStatus, Tone> = {
  counting: 'info',
  review: 'warning',
  approved: 'success',
  cancelled: 'neutral',
};

export const TOOL_STATE_TONE: Record<ToolState, Tone> = {
  ok: 'success',
  repair: 'warning',
  lost: 'danger',
  written_off: 'neutral',
};

export const CHECK_TONE: Record<ToolUnit['checkState'], Tone> = {
  none: 'neutral',
  ok: 'success',
  soon: 'warning',
  overdue: 'danger',
};
